import { Booking_Status_Enum, User_Roles_Enum, User_Type_Enum } from '../state/gql/_types';
import type { UserFragment, BookingFragment } from '../state/gql/_types';

/**
 * State machine context containing the current user and booking
 */
export interface BookingStatusContext {
    currentUser: UserFragment | null;
    currentBooking: BookingFragment | null;
}

/**
 * Events that trigger transitions in the booking status machine
 */
export type BookingStatusEvent =
    | { type: 'OFFER' }
    | { type: 'CANCEL' }
    | { type: 'ACCEPT' }
    | { type: 'DECLINE' }
    | { type: 'TALENT_CANCEL' }
    | { type: 'VENUE_CANCEL' }
    | { type: 'APPROVE' }
    | { type: 'DENY' }
    | { type: 'WILL_NOSHOW' }
    | { type: 'REASSIGN' }
    | { type: 'TALENT_ADVANCE' }
    | { type: 'BOOKER_ADVANCE' }
    | { type: 'BOOKER_ADVANCE_TO_TALENT' }
    | { type: 'RECONFIRM_BOOKING' }
    | { type: 'START' }
    | { type: 'CONFIRM' }
    | { type: 'COMPLETE' }
    | { type: 'INVOICE' }
    | { type: 'PAY' }
    | { type: 'PAY_OUT' };

/**
 * States in the booking status machine
 */
export type BookingStatusState =
    | { value: 'draft'; context: BookingStatusContext }
    | { value: 'offered'; context: BookingStatusContext }
    | { value: 'cancelled'; context: BookingStatusContext }
    | { value: 'accepted'; context: BookingStatusContext }
    | { value: 'declined'; context: BookingStatusContext }
    | { value: 'talent_cancelled'; context: BookingStatusContext }
    | { value: 'cancellation_approved'; context: BookingStatusContext }
    | { value: 'cancellation_denied'; context: BookingStatusContext }
    | { value: 'noshow'; context: BookingStatusContext }
    | { value: 'venue_cancelled'; context: BookingStatusContext }
    | { value: 'advanced'; context: BookingStatusContext }
    | { value: 'booker_advancing'; context: BookingStatusContext }
    | { value: 'talent_advancing'; context: BookingStatusContext }
    | { value: 'confirmed'; context: BookingStatusContext }
    | { value: 'active'; context: BookingStatusContext }
    | { value: 'completed'; context: BookingStatusContext }
    | { value: 'cancellation'; context: BookingStatusContext }
    | { value: 'invoiced'; context: BookingStatusContext }
    | { value: 'paid'; context: BookingStatusContext }
    | { value: 'paid_out'; context: BookingStatusContext };

/**
 * Type for the state value string
 */
export type BookingStatusStateValue =
    | 'draft'
    | 'offered'
    | 'cancelled'
    | 'accepted'
    | 'declined'
    | 'talent_cancelled'
    | 'cancellation_approved'
    | 'cancellation_denied'
    | 'noshow'
    | 'venue_cancelled'
    | 'advanced'
    | 'booker_advancing'
    | 'talent_advancing'
    | 'confirmed'
    | 'active'
    | 'completed'
    | 'cancellation'
    | 'invoiced'
    | 'paid'
    | 'paid_out';

/**
 * Input parameters for the state machine
 */
export interface BookingStatusInput {
    currentUser: UserFragment | null;
    currentBooking: BookingFragment | null;
}

/**
 * Guard parameter types
 */
export interface GuardParams {
    context: BookingStatusContext;
    event: BookingStatusEvent;
}

/**
 * Action parameter types
 */
export interface ActionParams {
    context: BookingStatusContext;
    event: BookingStatusEvent;
}

/**
 * Type for update booking function
 */
export type UpdateBookingFn = (params: {
    id: string;
    _set: { status: Booking_Status_Enum };
}) => Promise<BookingFragment | null>;

/**
 * Transition metadata for UI rendering
 */
export interface TransitionMetadata {
    event: BookingStatusEvent['type'];
    label: string;
    color?: string;
    variant?: 'filled' | 'light' | 'outline' | 'subtle' | 'default' | 'white' | 'gradient';
    description?: string;
    cta: string;
    /** Direction of the transition: 'forward' progresses the booking, 'backward' regresses/cancels */
    direction: 'forward' | 'backward';
}

/**
 * Mapping of event types to transition metadata
 */
export const TRANSITION_METADATA: Record<BookingStatusEvent['type'], TransitionMetadata> = {
    OFFER: {
        event: 'OFFER',
        label: 'Send Offer',
        color: 'blue',
        variant: 'filled',
        description: 'Send booking offer to talent',
        direction: 'forward',
        cta: 'Offer'
    },
    CANCEL: {
        event: 'CANCEL',
        label: 'Cancel',
        color: 'red',
        variant: 'outline',
        description: 'Cancel this booking',
        direction: 'backward',
        cta: 'Cancel'
    },
    ACCEPT: {
        event: 'ACCEPT',
        label: 'Accept',
        color: 'green',
        variant: 'filled',
        description: 'Accept the booking offer',
        direction: 'forward',
        cta: 'Accept'
    },
    DECLINE: {
        event: 'DECLINE',
        label: 'Decline',
        color: 'red',
        variant: 'light',
        description: 'Decline the booking offer',
        direction: 'backward',
        cta: 'Decline'
    },
    TALENT_CANCEL: {
        event: 'TALENT_CANCEL',
        label: 'Talent Cancel',
        color: 'orange',
        variant: 'filled',
        description: 'Talent requests cancellation',
        direction: 'backward',
        cta: 'Cancel'
    },
    VENUE_CANCEL: {
        event: 'VENUE_CANCEL',
        label: 'Venue Cancel',
        color: 'orange',
        variant: 'outline',
        description: 'Venue cancels the booking',
        direction: 'backward',
        cta: 'Cancel'
    },
    APPROVE: {
        event: 'APPROVE',
        label: 'Approve Cancellation',
        color: 'green',
        variant: 'filled',
        description: 'Approve the talent cancellation request',
        direction: 'backward',
        cta: 'Approve'
    },
    DENY: {
        event: 'DENY',
        label: 'Deny Cancellation',
        color: 'red',
        variant: 'filled',
        description: 'Deny the talent cancellation request',
        direction: 'forward',
        cta: 'Deny'
    },
    WILL_NOSHOW: {
        event: 'WILL_NOSHOW',
        label: 'No Show',
        color: 'red',
        variant: 'filled',
        description: 'Mark talent as no-show',
        direction: 'backward',
        cta: 'No Show'
    },
    REASSIGN: {
        event: 'REASSIGN',
        label: 'Reassign',
        color: 'blue',
        variant: 'light',
        description: 'Reassign booking to a new talent',
        direction: 'backward',
        cta: 'Reassign'
    },
    TALENT_ADVANCE: {
        event: 'TALENT_ADVANCE',
        label: 'Advance',
        color: 'blue',
        variant: 'filled',
        description: 'Advance booking to next stage',
        direction: 'forward',
        cta: 'Advance'
    },
    BOOKER_ADVANCE: {
        event: 'BOOKER_ADVANCE',
        label: 'Advance',
        color: 'blue',
        variant: 'filled',
        description: 'Advance booking to next stage',
        direction: 'forward',
        cta: 'Advance'
    },
    BOOKER_ADVANCE_TO_TALENT: {
        event: 'BOOKER_ADVANCE_TO_TALENT',
        label: 'Advance to Talent',
        color: 'blue',
        variant: 'filled',
        description: 'Advance booking to talent',
        direction: 'forward',
        cta: 'Advance'
    },
    RECONFIRM_BOOKING: {
        event: 'RECONFIRM_BOOKING',
        label: 'Reconfirm',
        color: 'green',
        variant: 'filled',
        description: 'Reconfirm booking for event day',
        direction: 'forward',
        cta: 'Reconfirm'
    },
    START: {
        event: 'START',
        label: 'Start',
        color: 'teal',
        variant: 'filled',
        description: 'Start the booking event',
        direction: 'forward',
        cta: 'Start'
    },
    CONFIRM: {
        event: 'CONFIRM',
        label: 'Confirm Cancellation',
        color: 'red',
        variant: 'filled',
        description: 'Confirm the venue cancellation',
        direction: 'backward',
        cta: 'Confirm'
    },
    COMPLETE: {
        event: 'COMPLETE',
        label: 'Mark Complete',
        color: 'teal',
        variant: 'filled',
        description: 'Mark booking as completed',
        direction: 'forward',
        cta: 'Complete'
    },
    INVOICE: {
        event: 'INVOICE',
        label: 'Send Invoice',
        color: 'indigo',
        variant: 'filled',
        description: 'Generate and send invoice',
        direction: 'forward',
        cta: 'Invoice'
    },
    PAY: {
        event: 'PAY',
        label: 'Mark Paid',
        color: 'green',
        variant: 'filled',
        description: 'Mark invoice as paid',
        direction: 'forward',
        cta: 'Pay'
    },
    PAY_OUT: {
        event: 'PAY_OUT',
        label: 'Pay Out',
        color: 'green',
        variant: 'filled',
        description: 'Pay out to talent',
        direction: 'forward',
        cta: 'Pay Out'
    },
};

/**
 * Role-based permissions
 */
export const ROLE_PERMISSIONS: Record<
    User_Roles_Enum,
    Partial<Record<BookingStatusEvent['type'], boolean>>
> = {
    [User_Roles_Enum.Superadmin]: {
        OFFER: true,
        CANCEL: true,
        ACCEPT: true,
        DECLINE: true,
        TALENT_CANCEL: true,
        VENUE_CANCEL: true,
        APPROVE: true,
        DENY: true,
        WILL_NOSHOW: true,
        REASSIGN: true,
        TALENT_ADVANCE: true,
        BOOKER_ADVANCE: true,
        BOOKER_ADVANCE_TO_TALENT: true,
        RECONFIRM_BOOKING: true,
        START: true,
        CONFIRM: true,
        COMPLETE: true,
        INVOICE: true,
        PAY: true,
        PAY_OUT: true,
    },
    [User_Roles_Enum.Administrator]: {
        OFFER: true,
        CANCEL: true,
        ACCEPT: true,
        DECLINE: true,
        TALENT_CANCEL: true,
        VENUE_CANCEL: true,
        APPROVE: true,
        DENY: true,
        WILL_NOSHOW: true,
        REASSIGN: true,
        TALENT_ADVANCE: true,
        BOOKER_ADVANCE: true,
        BOOKER_ADVANCE_TO_TALENT: true,
        RECONFIRM_BOOKING: true,
        START: true,
        CONFIRM: true,
        COMPLETE: true,
        INVOICE: true,
        PAY: true,
        PAY_OUT: true,
    },
    [User_Roles_Enum.User]: {
        OFFER: true,
        CANCEL: true,
        ACCEPT: true,
        DECLINE: true,
        TALENT_CANCEL: true,
        VENUE_CANCEL: true,
        APPROVE: true,
        DENY: true,
        WILL_NOSHOW: true,
        REASSIGN: true,
        TALENT_ADVANCE: true,
        BOOKER_ADVANCE: true,
        RECONFIRM_BOOKING: true,
        START: true,
        CONFIRM: true,
        COMPLETE: true,
        INVOICE: true,
        PAY: true,
        PAY_OUT: true,
    },
    [User_Roles_Enum.Guest]: {},
    [User_Roles_Enum.AgencyOperationsAdministrator]: {
        OFFER: true,
        CANCEL: true,
        ACCEPT: true,
        DECLINE: true,
        TALENT_CANCEL: true,
        VENUE_CANCEL: true,
        APPROVE: true,
        DENY: true,
        WILL_NOSHOW: true,
        REASSIGN: true,
        TALENT_ADVANCE: false,
        BOOKER_ADVANCE: true,
        BOOKER_ADVANCE_TO_TALENT: true,
        RECONFIRM_BOOKING: false,
        START: true,
        CONFIRM: true,
        COMPLETE: true,
        INVOICE: true,
        PAY: true,
        PAY_OUT: true,
    },
    [User_Roles_Enum.AgencyTalentAdministrator]: {
        ACCEPT: true,
        DECLINE: true,
        TALENT_CANCEL: true,
        TALENT_ADVANCE: true,
        RECONFIRM_BOOKING: true,
        START: true,
        COMPLETE: true,
        WILL_NOSHOW: true,
    },
    [User_Roles_Enum.VenueOperationsAdministrator]: {
        VENUE_CANCEL: true,
    },
    [User_Roles_Enum.AgencyOperationsUser]: {},
    [User_Roles_Enum.AgencyAccountingAdministrator]: {},
    [User_Roles_Enum.AgencyAccountingUser]: {},
    [User_Roles_Enum.AgencyMarketingAdministrator]: {},
    [User_Roles_Enum.AgencyMarketingUser]: {},
    [User_Roles_Enum.AgencyTalentUser]: {},
    [User_Roles_Enum.AgencyBookerAdministrator]: {},
    [User_Roles_Enum.AgencyBookerUser]: {},
    [User_Roles_Enum.AgencyDoorAdministrator]: {},
    [User_Roles_Enum.AgencyDoorUser]: {},
    [User_Roles_Enum.VenueOperationsUser]: {},
    [User_Roles_Enum.VenueAccountingAdministrator]: {},
    [User_Roles_Enum.VenueAccountingUser]: {},
    [User_Roles_Enum.VenueMarketingAdministrator]: {},
    [User_Roles_Enum.VenueMarketingUser]: {},
    [User_Roles_Enum.VenueTalentAdministrator]: {},
    [User_Roles_Enum.VenueTalentUser]: {},
    [User_Roles_Enum.VenueBookerAdministrator]: {},
    [User_Roles_Enum.VenueBookerUser]: {},
    [User_Roles_Enum.VenueDoorAdministrator]: {},
    [User_Roles_Enum.VenueDoorUser]: {},
};

/**
 * Type-based permissions
 */
export const TYPE_PERMISSIONS: Record<
    User_Type_Enum,
    Partial<Record<BookingStatusEvent['type'], boolean>>
> = {
    [User_Type_Enum.Booker]: {
        OFFER: true,
        CANCEL: true,
        VENUE_CANCEL: true,
        APPROVE: true,
        DENY: true,
        WILL_NOSHOW: true,
        REASSIGN: true,
        BOOKER_ADVANCE: true,
        BOOKER_ADVANCE_TO_TALENT: true,
        RECONFIRM_BOOKING: true,
        CONFIRM: true,
    },
    [User_Type_Enum.Talent]: {
        ACCEPT: true,
        DECLINE: true,
        TALENT_CANCEL: true,
        WILL_NOSHOW: true,
        TALENT_ADVANCE: true,
        RECONFIRM_BOOKING: true,
        START: true,
        COMPLETE: true,
    },
    [User_Type_Enum.Operations]: {
        OFFER: true,
        CANCEL: true,
        VENUE_CANCEL: true,
        APPROVE: true,
        DENY: true,
        WILL_NOSHOW: true,
        REASSIGN: true,
        TALENT_ADVANCE: true,
        BOOKER_ADVANCE: true,
        BOOKER_ADVANCE_TO_TALENT: true,
        RECONFIRM_BOOKING: true,
        START: true,
        COMPLETE: true,
        CONFIRM: true,
        INVOICE: true,
        PAY: true,
        PAY_OUT: true,
    },
    [User_Type_Enum.Accounting]: {
        INVOICE: true,
        PAY: true,
        PAY_OUT: true,
        WILL_NOSHOW: true,
    },
    [User_Type_Enum.Marketing]: {},
    [User_Type_Enum.Door]: {},
};

/**
 * Check if a user has permission for a specific event.
 *
 * - Superadmin: only checks ROLE_PERMISSIONS (bypasses type).
 * - All others: both ROLE_PERMISSIONS[role][event] AND TYPE_PERMISSIONS[type][event] must be true.
 */
export function hasPermission(
    role: User_Roles_Enum,
    type: User_Type_Enum,
    event: BookingStatusEvent['type'],
): boolean {
    const roleAllowed = ROLE_PERMISSIONS[role]?.[event] === true;
    if (!roleAllowed) return false;

    // Superadmin bypasses type check
    if (role === User_Roles_Enum.Superadmin) return true;

    return TYPE_PERMISSIONS[type]?.[event] === true;
}

/**
 * Display metadata for each booking status state
 */
export interface StateMetadata {
    label: string;
    description: string;
    icon: string;
    isFinal: boolean;
    isBranch: boolean;
}

export const STATE_METADATA: Record<BookingStatusStateValue, StateMetadata> = {
    draft:                 { label: 'Draft',                 description: 'Booking is being prepared',         icon: 'ph:pencil-simple',      isFinal: false, isBranch: false },
    offered:               { label: 'Offered',               description: 'Offer sent to talent',              icon: 'ph:paper-plane-tilt',   isFinal: false, isBranch: false },
    accepted:              { label: 'Accepted',              description: 'Talent accepted the offer',         icon: 'ph:thumbs-up',          isFinal: false, isBranch: false },
    declined:              { label: 'Declined',              description: 'Talent declined the offer',         icon: 'ph:thumbs-down',        isFinal: false, isBranch: true },
    advanced:              { label: 'Advanced',              description: 'Pre-event staging phase',           icon: 'ph:fast-forward',       isFinal: false, isBranch: false },
    confirmed:             { label: 'Confirmed',             description: 'Booking confirmed for event',       icon: 'ph:seal-check',         isFinal: false, isBranch: false },
    active:                { label: 'Active',                description: 'Event is in progress',              icon: 'ph:play-circle',        isFinal: false, isBranch: false },
    completed:             { label: 'Completed',             description: 'Event completed',                   icon: 'ph:check-circle',       isFinal: false, isBranch: false },
    invoiced:              { label: 'Invoiced',              description: 'Invoice sent',                      icon: 'ph:receipt',            isFinal: false, isBranch: false },
    paid:                  { label: 'Paid',                  description: 'Payment received',                  icon: 'ph:credit-card',        isFinal: false, isBranch: false },
    paid_out:              { label: 'Paid Out',              description: 'Talent has been paid',              icon: 'ph:money',              isFinal: true,  isBranch: false },
    cancelled:             { label: 'Cancelled',             description: 'Booking cancelled from draft',      icon: 'ph:x-circle',           isFinal: true,  isBranch: true },
    talent_cancelled:      { label: 'Talent Cancelled',      description: 'Talent requested cancellation',     icon: 'ph:user-minus',         isFinal: false, isBranch: true },
    cancellation_approved: { label: 'Cancellation Approved', description: 'Talent cancellation approved',      icon: 'ph:check-square',       isFinal: false, isBranch: true },
    cancellation_denied:   { label: 'Cancellation Denied',   description: 'Talent cancellation denied',        icon: 'ph:prohibit',           isFinal: false, isBranch: true },
    noshow:                { label: 'No Show',               description: 'Talent marked as no-show',          icon: 'ph:user-circle-minus',  isFinal: false, isBranch: true },
    venue_cancelled:       { label: 'Venue Cancelled',       description: 'Venue cancelled the booking',       icon: 'ph:storefront',         isFinal: false, isBranch: true },
    cancellation:          { label: 'Cancellation',          description: 'Venue cancellation confirmed',      icon: 'ph:x-square',           isFinal: true,  isBranch: true },
    booker_advancing:      { label: 'Booker Advancing',      description: 'Booker advancing to next stage',    icon: 'ph:fast-forward',       isFinal: false, isBranch: false },
    talent_advancing:      { label: 'Talent Advancing',      description: 'Talent advancing to next stage',    icon: 'ph:fast-forward',       isFinal: false, isBranch: false },
};

/**
 * Timeline step for rendering the booking progression
 */
export interface TimelineStep {
    state: BookingStatusStateValue;
    label: string;
    description: string;
    icon: string;
    status: 'past' | 'current' | 'future';
}

/**
 * Computed timeline path with steps and active index
 */
export interface TimelinePath {
    steps: TimelineStep[];
    activeIndex: number;
}

/**
 * Category of guard failure
 */
export type GuardFailureCategory = 'permission' | 'validation' | 'relationship';

/**
 * A single guard failure with category and user-facing message
 */
export interface GuardFailure {
    category: GuardFailureCategory;
    message: string;
}

/**
 * Diagnostic information for a transition that may be blocked
 */
export interface TransitionDiagnostic {
    event: BookingStatusEvent['type'];
    label: string;
    canTransition: boolean;
    failures: GuardFailure[];
}
